import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSession } from '@/features/auth/useSession'

import { PageHeader } from '@/components/PageHeader'
import {
  addObjective,
  closePlan,
  commitPlan,
  createPlan,
  fetchPlan,
  fetchPlans,
} from '@/features/planning/api'
import { CapacityPanel } from '@/features/planning/CapacityPanel'
import { CommitmentBoard } from '@/features/planning/CommitmentBoard'
import { ObjectiveList } from '@/features/planning/ObjectiveList'
import { RevisionRow } from '@/features/planning/RevisionRow'
import { fetchDivisions } from '@/features/portfolio/api'
import type { ApiError } from '@/lib/api/client'
import type { Division } from '@/types/domain'
import type { Plan, PlanStatus } from '@/types/planning'

const STATUS_CLASS: Record<PlanStatus, string> = {
  draft: 'border-line text-ink-500',
  committed: 'border-brand-300 bg-brand-50 text-brand-700',
  closed: 'border-[color:var(--color-state-done)] text-[color:var(--color-state-done)]',
}

/**
 * Planning: the annual and quarter horizons (ADR-0006).
 *
 * Iterations and per-person allocation are Phase 7 on purpose — iteration
 * planning against a board nobody uses daily produces a plan nobody follows.
 */
export function PlanningPage() {
  const { t } = useTranslation()
  // "No division" means the plan belongs to the whole organization, so it is
  // named rather than labelled with a constant from a translation file.
  const orgName = useSession().data?.organization.name ?? ''
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<string | null>(null)

  const plans = useQuery<Plan[], ApiError>({
    queryKey: ['plans'],
    queryFn: () => fetchPlans(),
    retry: false,
  })

  const divisions = useQuery<Division[], ApiError>({
    queryKey: ['divisions'],
    queryFn: fetchDivisions,
    retry: false,
  })

  // Land on the current period rather than making someone pick one. Derived
  // rather than written into state from an effect: the default is a function of
  // the data, and computing it during render avoids the extra pass — and the
  // flash of an unselected screen — that setting state in an effect costs.
  const today = new Date().toISOString().slice(0, 10)
  const fallback =
    plans.data?.find((plan) => plan.startsOn <= today && plan.endsOn >= today) ?? plans.data?.[0]
  const active = selected ?? fallback?.id ?? null

  const plan = useQuery<Plan, ApiError>({
    queryKey: ['plan', active],
    queryFn: () => fetchPlan(active as string),
    enabled: active !== null,
    retry: false,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['plans'] })
    void queryClient.invalidateQueries({ queryKey: ['plan', active] })
  }

  const draft = useMutation<Plan, ApiError, { divisionId: string | null; year: number; quarter: number }>({
    mutationFn: (input: { divisionId: string | null; year: number; quarter: number }) =>
      createPlan({
        divisionId: input.divisionId,
        horizon: 'quarter',
        periodYear: input.year,
        periodQuarter: input.quarter,
      }),
    onSuccess: (created) => {
      setSelected(created.id)
      refresh()
    },
  })

  const commit = useMutation({
    mutationFn: () => commitPlan(active as string),
    onSuccess: refresh,
  })

  const close = useMutation({
    mutationFn: () => closePlan(active as string),
    onSuccess: refresh,
  })

  const objective = useMutation({
    mutationFn: (title: string) => addObjective(active as string, { title }),
    onSuccess: refresh,
  })

  if (plans.isPending) {
    return <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
  }

  if (plans.isError) {
    return (
      <div>
        <PageHeader title={t('planning.title')} subtitle={t('planning.subtitle')} />
        <p className="text-ink-700 text-sm">{plans.error.message}</p>
      </div>
    )
  }

  return (
    <div>
      <PageHeader title={t('planning.title')} subtitle={t('planning.subtitle')} />

      <DraftPlanForm
        divisions={divisions.data ?? []}
        onDraft={(input) => draft.mutate(input)}
        isPending={draft.isPending}
        error={draft.error}
      />

      {plans.data.length === 0 ? (
        <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">
          {t('planning.noPlans')}
        </p>
      ) : (
        <ul className="mb-7 flex flex-wrap gap-2">
          {plans.data.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => setSelected(row.id)}
                aria-pressed={active === row.id}
                className={`rounded border px-3 py-1.5 text-sm ${
                  active === row.id
                    ? 'border-brand-600 bg-brand-600 text-white'
                    : 'border-line bg-surface text-ink-700 hover:bg-surface-2'
                }`}
              >
                <span className="font-mono text-xs">{row.period}</span>
                <span className="ms-2">{row.division ?? orgName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {plan.data === undefined ? null : (
        <>
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <h2 className="text-ink-900 text-lg font-semibold">
              {plan.data.division ?? orgName}
              <span className="text-ink-500 ms-2 font-mono text-sm">{plan.data.period}</span>
            </h2>

            <span
              className={`rounded border px-2 py-0.5 font-mono text-[10px] tracking-widest uppercase ${STATUS_CLASS[plan.data.status]}`}
            >
              {t(`planning.${plan.data.status}`)}
            </span>

            <div className="ms-auto flex gap-2">
              {plan.data.status === 'draft' ? (
                <button
                  type="button"
                  onClick={() => commit.mutate()}
                  disabled={commit.isPending}
                  className="bg-brand-600 rounded px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
                >
                  {t('planning.commit')}
                </button>
              ) : null}

              {plan.data.status === 'committed' ? (
                <button
                  type="button"
                  onClick={() => close.mutate()}
                  disabled={close.isPending}
                  className="border-line text-ink-700 hover:bg-surface-2 rounded border px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  {t('planning.close')}
                </button>
              ) : null}
            </div>
          </div>

          {plan.data.capacity === null ? null : <CapacityPanel capacity={plan.data.capacity} />}

          <section className="mb-7">
            <h2 className="text-ink-900 mb-3 text-sm font-semibold">{t('planning.objectives')}</h2>
            <ObjectiveList objectives={plan.data.objectives} planId={plan.data.id} />

            {plan.data.status === 'closed' ? null : (
              <form
                className="mt-3 flex flex-wrap gap-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  const form = event.currentTarget
                  const input = form.elements.namedItem('title') as HTMLInputElement
                  if (input.value.trim() !== '') {
                    objective.mutate(input.value.trim())
                    form.reset()
                  }
                }}
              >
                <input
                  name="title"
                  type="text"
                  placeholder={t('planning.addObjective')}
                  className="border-line bg-surface min-w-64 flex-1 rounded border px-2.5 py-1.5 text-sm"
                />
                <button
                  type="submit"
                  disabled={objective.isPending}
                  className="border-line text-ink-700 hover:bg-surface-2 rounded border px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  {t('planning.save')}
                </button>
              </form>
            )}
          </section>

          <CommitmentBoard plan={plan.data} />

          {plan.data.revisions.length === 0 ? null : (
            <section className="mt-7">
              <h2 className="text-ink-900 mb-3 text-sm font-semibold">
                {t('planning.revisions')}
              </h2>
              <ul className="border-line bg-surface divide-line divide-y rounded-md border text-sm">
                {plan.data.revisions.map((revision) => (
                  <RevisionRow key={revision.id} planId={plan.data.id} revision={revision} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  )
}

function DraftPlanForm({
  divisions,
  onDraft,
  isPending,
  error,
}: {
  divisions: Division[]
  onDraft: (input: { divisionId: string | null; year: number; quarter: number }) => void
  isPending: boolean
  error: ApiError | null
}) {
  const { t } = useTranslation()
  const orgName = useSession().data?.organization.name ?? ''
  const now = new Date()
  const [divisionId, setDivisionId] = useState('')
  const [year, setYear] = useState(String(now.getFullYear()))
  const [quarter, setQuarter] = useState(String(Math.floor(now.getMonth() / 3) + 1))

  return (
    <form
      className="border-line bg-surface mb-7 flex flex-wrap items-end gap-3 rounded-md border p-4"
      onSubmit={(event) => {
        event.preventDefault()
        onDraft({
          divisionId: divisionId === '' ? null : divisionId,
          year: Number(year),
          quarter: Number(quarter),
        })
      }}
    >
      <label className="min-w-48 flex-1">
        <span className="text-ink-500 mb-1 block font-mono text-[10px] tracking-widest uppercase">
          {t('portfolio.division')}
        </span>
        <select
          value={divisionId}
          onChange={(event) => setDivisionId(event.target.value)}
          className="border-line bg-surface w-full rounded border px-2.5 py-1.5 text-sm"
        >
          <option value="">{orgName}</option>
          {divisions.map((division) => (
            <option key={division.id} value={division.id}>
              {division.name}
            </option>
          ))}
        </select>
      </label>

      <label>
        <span className="text-ink-500 mb-1 block font-mono text-[10px] tracking-widest uppercase">
          {t('planning.year')}
        </span>
        <input
          type="number"
          value={year}
          onChange={(event) => setYear(event.target.value)}
          className="border-line w-24 rounded border px-2.5 py-1.5 font-mono text-sm tabular-nums"
        />
      </label>

      <label>
        <span className="text-ink-500 mb-1 block font-mono text-[10px] tracking-widest uppercase">
          {t('planning.quarter')}
        </span>
        <select
          value={quarter}
          onChange={(event) => setQuarter(event.target.value)}
          className="border-line bg-surface rounded border px-2.5 py-1.5 text-sm"
        >
          {[1, 2, 3, 4].map((value) => (
            <option key={value} value={value}>
              Q{value}
            </option>
          ))}
        </select>
      </label>

      <button
        type="submit"
        disabled={isPending}
        className="border-line text-ink-700 hover:bg-surface-2 rounded border px-3 py-1.5 text-sm disabled:opacity-40"
      >
        {t('planning.newPlan')}
      </button>

      {error === null ? null : (
        <p className="w-full text-sm text-[color:var(--color-state-blocked)]">{error.message}</p>
      )}
    </form>
  )
}
